import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { portalAuthApi } from '@/lib/api/portal.js';
import { usePortalAuthStore } from '@/stores/portal-auth.js';
import { parseApiError } from '@/lib/api/errors.js';

export function PortalLoginPage() {
  const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
  const setAuth = usePortalAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tenantFromUrl = (params.get('tenant') ?? params.get('store') ?? '').trim();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState(tenantFromUrl);
  const [showShopId, setShowShopId] = useState(Boolean(tenantFromUrl));
  const [error, setError] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: () =>
      portalAuthApi.login({
        email: email.trim(),
        password: password.trim(),
        ...(tenantSlug.trim() ? { tenantSlug: tenantSlug.trim() } : {}),
      }),
    onSuccess: (res) => {
      setAuth(
        {
          id: res.data.customer.id,
          firstName: res.data.customer.firstName,
          lastName: res.data.customer.lastName,
          email: res.data.customer.email,
          portalCanCreateTickets: res.data.customer.portalCanCreateTickets,
        },
        res.data.token,
      );
      navigate('/portal/dashboard');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      setError(parsed.message);
      if (parsed.code === 'SHOP_ID_REQUIRED') {
        setShowShopId(true);
      }
    },
  });

  if (isAuthenticated) return <Navigate to="/portal/dashboard" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-sky-100 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <h1 className="text-2xl font-bold text-[#1B4F8A]">Customer Portal</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in with the email and temporary password from your welcome email</p>

        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            loginMutation.mutate();
          }}
        >
          <label className="block text-sm font-medium text-slate-700">
            Email
            <input
              type="email"
              autoComplete="username"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Password
            <input
              type="password"
              autoComplete="current-password"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          {showShopId ? (
            <label className="block text-sm font-medium text-slate-700">
              Shop ID
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                placeholder="From your welcome email"
              />
              <span className="mt-1 block text-xs font-normal text-slate-500">
                Only needed if you have accounts at more than one shop. It was included in your welcome email.
              </span>
            </label>
          ) : (
            <button
              type="button"
              className="text-xs font-semibold text-slate-500 hover:text-slate-800"
              onClick={() => setShowShopId(true)}
            >
              Need a Shop ID?
            </button>
          )}

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white hover:bg-[#163f6e] disabled:opacity-60"
          >
            {loginMutation.isPending ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 flex justify-between text-sm">
          <Link to="/portal/forgot-password" className="font-semibold text-[#1B4F8A]">
            Forgot password?
          </Link>
          <Link to="/track" className="text-slate-500 hover:text-slate-800">
            Track without login
          </Link>
        </div>
      </div>
    </div>
  );
}
